import { useEffect, useState } from "react";

import {
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  deleteUserSurveyAccess,
  getOrgSurveyConfigPortal,
  getUserSurveyAccess,
  putUserSurveyAccess,
} from "@rubi/api-generated";
import { toast } from "@rubi/ui";

import { RubiModal } from "@/common/RubiModal";

interface SurveyAccessEntry {
  survey_classes: string;
  restrict_access_to_surveys: boolean;
}

interface ReportType {
  survey_class: string;
  name: string;
}

const ReportAccessListModal = ({
  isOpen,
  onClose,
  userId,
  orgId,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  orgId: string;
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accessMode, setAccessMode] = useState<"all" | "custom">("all");
  const [reportTypes, setReportTypes] = useState<ReportType[]>([]);
  const [visibilityMap, setVisibilityMap] = useState<Record<string, boolean>>(
    {},
  );
  const [hadExistingEntry, setHadExistingEntry] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setLoading(true);

    const loadData = async () => {
      try {
        const [accessResponse, configResponse] = await Promise.all([
          getUserSurveyAccess({
            path: { org_id: orgId, user_id: userId },
          }).catch(() => ({ data: null })),
          getOrgSurveyConfigPortal({ path: { org_id: orgId } }),
        ]);

        const surveys = configResponse.data?.surveys ?? [];
        const uniqueClasses = new Map<string, string>();
        for (const survey of surveys) {
          if (!uniqueClasses.has(survey.survey_class)) {
            uniqueClasses.set(survey.survey_class, survey.name);
          }
        }
        const types: ReportType[] = Array.from(uniqueClasses.entries()).map(
          ([survey_class, name]) => ({ survey_class, name }),
        );
        setReportTypes(types);

        const initialVisibility: Record<string, boolean> = {};
        for (const rt of types) {
          initialVisibility[rt.survey_class] = true;
        }

        const accessEntry = accessResponse.data as SurveyAccessEntry | null;
        if (accessEntry && accessEntry.survey_classes !== undefined) {
          setHadExistingEntry(true);
          setAccessMode("custom");
          const classes = accessEntry.survey_classes
            .split(",")
            .filter((s) => s.length > 0);
          if (accessEntry.restrict_access_to_surveys) {
            // Blacklist mode: listed classes are hidden
            for (const cls of classes) {
              initialVisibility[cls] = false;
            }
          } else {
            // Whitelist mode: only listed classes are visible
            for (const rt of types) {
              initialVisibility[rt.survey_class] = false;
            }
            for (const cls of classes) {
              initialVisibility[cls] = true;
            }
          }
        } else {
          setHadExistingEntry(false);
          setAccessMode("all");
        }

        setVisibilityMap(initialVisibility);
      } catch (error) {
        console.error("Failed to load survey access data", error);
        toast.error("Failed to load report access data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isOpen, orgId, userId]);

  const handleToggle = (surveyClass: string) => {
    setVisibilityMap((prev) => ({
      ...prev,
      [surveyClass]: !prev[surveyClass],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (accessMode === "all") {
        if (hadExistingEntry) {
          const { error } = await deleteUserSurveyAccess({
            path: { org_id: orgId, user_id: userId },
          });
          if (error) throw error;
        }
      } else {
        const visibleClasses = reportTypes
          .filter((rt) => visibilityMap[rt.survey_class])
          .map((rt) => rt.survey_class);
        const hiddenClasses = reportTypes
          .filter((rt) => !visibilityMap[rt.survey_class])
          .map((rt) => rt.survey_class);

        let survey_classes: string;
        let restrict_access_to_surveys: boolean;

        if (hiddenClasses.length <= visibleClasses.length) {
          // Blacklist mode: store hidden classes
          restrict_access_to_surveys = true;
          survey_classes = hiddenClasses.join(",");
        } else {
          // Whitelist mode: store visible classes
          restrict_access_to_surveys = false;
          survey_classes = visibleClasses.join(",");
        }

        const { error } = await putUserSurveyAccess({
          path: { org_id: orgId, user_id: userId },
          body: { survey_classes, restrict_access_to_surveys },
        });
        if (error) throw error;
      }
      toast.success("Report access updated successfully");
      onClose();
    } catch (error) {
      console.error("Failed to save survey access", error);
      toast.error("Failed to save report access");
    } finally {
      setSaving(false);
    }
  };

  return (
    <RubiModal
      isModalOpenArg={isOpen}
      setIsModalOpenArg={() => onClose()}
      onClose={onClose}
    >
      <Typography variant="h6" style={{ marginBottom: "16px" }}>
        Report Access List
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <RadioGroup
            value={accessMode}
            onChange={(e) => setAccessMode(e.target.value as "all" | "custom")}
          >
            <FormControlLabel
              value="all"
              control={<Radio />}
              label="All reports (default)"
            />
            <FormControlLabel
              value="custom"
              control={<Radio />}
              label="Custom access"
            />
          </RadioGroup>

          <TableContainer
            style={{ marginTop: "16px", maxHeight: "400px", overflow: "auto" }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Report Type</TableCell>
                  <TableCell align="right">Access</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportTypes.map((rt) => (
                  <TableRow key={rt.survey_class}>
                    <TableCell>{rt.name}</TableCell>
                    <TableCell align="right">
                      <Switch
                        checked={
                          accessMode === "all"
                            ? true
                            : (visibilityMap[rt.survey_class] ?? true)
                        }
                        onChange={() => handleToggle(rt.survey_class)}
                        disabled={accessMode === "all"}
                        color="primary"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "8px",
              marginTop: "16px",
            }}
          >
            <Button variant="outlined" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} /> : "Save"}
            </Button>
          </div>
        </>
      )}
    </RubiModal>
  );
};

export default ReportAccessListModal;
